import React, { Component } from 'react';
import { compose } from 'recompose';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  Loader,
  Card,
} from 'semantic-ui-react';

import BasicHeader from '../../components/basic-header';
import Account from '../../components/token-generation-card';
import TokenMediaModal from '../../components/token-media-modal';
import MissingPermissions from './missing-permissions';
import NoLinkedAccounts from './no-linked-accounts';
import ThemeSelector from '../../themes/theme-selector';

import hasRESTClient from '../../hocs/has-rest-client';
import hasUserInfo from '../../hocs/has-user-info';

const THEME = ThemeSelector.theme();

const FACEBOOK_OPTIONS = {
  clientId: '307083449705342',
};

const REQUIRED_PERMISSIONS = [
  'manage_pages',
  'instagram_basic',
  'instagram_manage_insights',
];

// the response has changed, but could be a glitch!?
const NEW_REQUIRED_PERMISSIONS = [
  'pages_show_list',
  'instagram_basic',
  'instagram_manage_insights',
];

const facebookCallbackUrl = 'https://uiqcrs8dhb.execute-api.eu-west-1.amazonaws.com/production/callback/facebook';

const getFacebookOptions = () => {
  const clientId = THEME.facebookClientId;
  return clientId
    ? { clientId }
    : FACEBOOK_OPTIONS;
};

const ACCOUNT_AUTO_PROCESSING_TIMEOUT = 500;


class TokenGenerationPage extends Component {
  constructor() {
    super();
    this.state = {
      accounts: [],
      declinedPermissions: [],
      grantedPermissions: [],
      isLoading: true,
      tokenMediaModalOpen: false,
      selectedTokenMediaId: null,
      tokenConfirmationInProgress: false,
    };
  }

  async componentDidMount() {
    const {
      oauthTokenAPIClient,
      restClient,
      routeParams: { tokenId },
    } = this.props;
    const oauthResourceResponse = await restClient.get(`oauthresponses/${tokenId}`);
    const oauthFetchFailed = _.isUndefined(oauthResourceResponse);
    const oauthResource = oauthFetchFailed ? null : _.get(oauthResourceResponse, 'data.data');
    const response = await oauthTokenAPIClient.get(`oauthresponse/${tokenId}`);
    if (response) {
      const { data: { data } } = response;
      const isExactMatch = _.get(data, 'isExactMatch', false);
      this.setState({
        isLoading: false,
        oauthResource,
        ...data,
      }, () => {
        if (isExactMatch) {
          setTimeout(async () => {
            const account = _.head(_.get(data, 'accounts'));
            const id = _.get(account, 'id');
            if (!id) return;
            await this.onAccountConfirmed({
              accountId: id,
            });
          }, ACCOUNT_AUTO_PROCESSING_TIMEOUT);
        }
      });
    }
  }

  onAccountSelected = id => {
    this.setState({
      tokenMediaModalOpen: true,
      selectedTokenMediaId: id,
    });
  }

  onMediaModalClose = () => {
    this.setState({
      tokenMediaModalOpen: false,
      selectedTokenMediaId: null,
    });
  }

  authoriseInstagram = () => {
    const instagramScopes = [
      'manage_pages',
      'instagram_manage_insights',
      'instagram_basic',
    ];
    this.authorise('instagram', instagramScopes);
  }

  authorise = (platform, scopeList) => {
    const { influencerId } = this.state;
    const { hostname, origin } = window.location;
    const callbackURL = `${origin}/token/generate`;
    const scopes = _.join(scopeList, ',');
    const version = 2;

    const state = encodeURIComponent(
      JSON.stringify({
        influencerId,
        callbackURL,
        platform,
        hostname,
        version,
      }),
    );

    const facebookOptions = getFacebookOptions();
    const opts = [
      'https://www.facebook.com/v2.11/dialog/oauth',
      `?client_id=${facebookOptions.clientId}`,
      '&response_type=code',
      `&redirect_uri=${facebookCallbackUrl}`,
      `&scope=${scopes}`,
      '&auth_type=rerequest',
      `&state=${state}`,
    ];
    window.location.href = _.join(opts, '');
  }

  onAccountConfirmed = async ({ accountId }) => {
    this.setState({
      tokenConfirmationInProgress: true,
    }, async () => {
      const {
        userInfo,
        routeParams: {
          tokenId: oauthResourceId,
        },
        oauthTokenAPIClient,
        router,
      } = this.props;

      const { name } = userInfo;
      const tokenData = {
        accountId,
        oauthResourceId,
        createdBy: name,
      };

      await oauthTokenAPIClient.post('oauthresponse/create-token', {
        ...tokenData,
      }).catch(() => {
        this.setState({
          tokenConfirmationInProgress: false,
        });
      });

      const remainingAccounts = _.reject(this.state.accounts, ['id', accountId]);

      if (_.isEmpty(remainingAccounts)) {
        router.push('/influencer/authorisation');
      }

      this.setState({
        tokenMediaModalOpen: false,
        selectedTokenMediaId: null,
        accounts: remainingAccounts,
        tokenConfirmationInProgress: false,
      });
    });
  }

  arePermissionsOK = ({ grantedPermissions }) => {
    console.log(grantedPermissions);
    const granted = _.toArray(_.mapValues(grantedPermissions, 'permission'));
    const ok = _.intersection(granted, REQUIRED_PERMISSIONS).length === REQUIRED_PERMISSIONS.length
      || _.intersection(granted, NEW_REQUIRED_PERMISSIONS).length === NEW_REQUIRED_PERMISSIONS.length;
    return ok;
  }

  render() {
    const { routeParams: { tokenId } } = this.props;
    const {
      isLoading,
      accounts,
      declinedPermissions,
      grantedPermissions,
      tokenMediaModalOpen,
      selectedTokenMediaId,
      tokenConfirmationInProgress,
    } = this.state;

    const arePermissionsOK = this.arePermissionsOK({ declinedPermissions, grantedPermissions });
    const noAccounts = _.isEmpty(accounts);

    const margin30 = {
      margin: 30,
    };
    return (
      <div className="page">
        <BasicHeader
          title={'Instagram Authorisation'}
        />
        <div className="page-content">
          {
            isLoading && <Loader active content="Loading" />
          }
          {
            !isLoading && !arePermissionsOK && (
              <MissingPermissions
                declinedPermissions={declinedPermissions}
                onContinueClick={this.authoriseInstagram}
              />
            )
          }
          {
            !isLoading && arePermissionsOK && !noAccounts && (
              <div style={margin30}>
                <h3>Please select your Instagram account from the list below</h3>
                <p>Then check your content and hit confirm to continue</p>

                <Card.Group itemsPerRow={4} style={margin30}>
                  {
                    _.map(accounts, account => (
                      <Account
                        {...account}
                        onClick={this.onAccountSelected}
                      />
                    ))
                  }
                </Card.Group>
              </div>
            )
          }
          {
            !isLoading && arePermissionsOK && noAccounts && (
              <NoLinkedAccounts
                declinedPermissions={declinedPermissions}
                onContinueClick={this.authoriseInstagram}
              />
            )
          }
        </div>
        {
          tokenMediaModalOpen &&
          <TokenMediaModal
            tokenConfirmationInProgress={tokenConfirmationInProgress}
            onConfirm={this.onAccountConfirmed}
            onClose={this.onMediaModalClose}
            oauthResponseId={tokenId}
            accountId={selectedTokenMediaId}
          />
        }
      </div>
    );
  }
}

TokenGenerationPage.propTypes = {
  routeParams: PropTypes.object.isRequired,
};

export default compose(
  hasRESTClient,
  hasUserInfo,
  withRouter,
)(TokenGenerationPage);



/** WEBPACK FOOTER **
 ** ./src/containers/token-generation-page/index.js
 **/